import { Divider, DialogContent, Button } from '@mui/material';
import AppSimpleDialog from 'app/shared-components/dialogs/AppSimpleDialog';
import React, { useState, useEffect } from 'react';
import './DKPopup.css';
import FormInput from 'app/shared-components/formComponents/FormInput';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { $authHost } from 'app/configs/axiosConfig';
import useAuthUser from 'src/app/hooks/useAuthUser';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSnackbar } from 'notistack';
import { DKPopupContent } from 'app/shared-components/dkPopup/DKPopupContent';
import { DKPopupSchema, type DKPopupProps, type DKPopupIFormValues } from './types';

const Popup = ({ open, handleClose: handleCloseExternal, bannerData }: DKPopupProps) => {
  const [showForm, setShowForm] = useState(false);
  const user = useAuthUser();
  const { enqueueSnackbar } = useSnackbar();

  const handleClose = (submitted: boolean) => {
    handleCloseExternal(submitted);
  };

  const methods = useForm<DKPopupIFormValues>({
    mode: 'onChange',
    resolver: yupResolver(DKPopupSchema),
  });

  if (!bannerData) {
    return (
      <AppSimpleDialog
        open={open}
        handleClose={() => handleClose(false)}
        title=" "
        size="md"
        scroll="paper"
        maxWidth="sm"
      >
        <DialogContent className="px-32">
          <div>Loading banner data...</div>
        </DialogContent>
      </AppSimpleDialog>
    );
  }

  useEffect(() => {
    if (open) {
      $authHost
        .patch('/banner', { viewedPopup: true })
        .then((response) => {})
        .catch((error) => {
          console.error('Error sending viewedPopup:', error);
        });
    }
  }, [open]);

  const {
    control,
    handleSubmit,
    formState: { errors, isValid, isSubmitting },
  } = methods;

  const onSubmit = (data: DKPopupIFormValues) => {
    $authHost
      .patch('/banner', data)
      .then((response) => {
        enqueueSnackbar('Message sent successfully!', { variant: 'successToast' });
        handleClose(true);
      })
      .catch((error) => {
        enqueueSnackbar('Failed to send message.', { variant: 'errorToast' });
        handleClose(false);
      });
  };

  return (
    <AppSimpleDialog
      open={open}
      handleClose={() => handleClose(false)}
      title=" "
      size="md"
      scroll="paper"
      maxWidth="sm"
      closeButtonClassName="dk-popup-close-btn"
      titleClassName="dk-title"
    >
      <Divider />
      <DialogContent className="px-32">
        {!showForm && <DKPopupContent setShowForm={setShowForm} />}

        {showForm && (
          <div className="mt-12">
            <h2 className="text-[24px] font-semibold text-gray-900 mb-16">Apply now</h2>
            <FormProvider {...methods}>
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="mb-24 mt-40">
                  <Controller
                    name="email"
                    control={control}
                    defaultValue={user?.email || ''}
                    render={({ field }) => (
                      <FormInput
                        {...field}
                        id="email"
                        label="Your email"
                        placeholder="example@businessmail.com"
                        errorMessage={errors.email?.message?.toString()}
                        variant="outlined"
                        fullWidth
                        type="email"
                        control={control}
                      />
                    )}
                  />
                  <p className="text-gray-500 text-xs mt-4 flex gap-2">
                    <img
                      src="/assets/icons/information-circle.svg"
                      alt="information-circle"
                      className="icon-sm"
                    />{' '}
                    Automatically we use your registration email, but you can change it.
                  </p>
                </div>

                <div className="mb-24">
                  <Controller
                    name="message"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <FormInput
                        {...field}
                        id="message"
                        label="Message"
                        placeholder="Type here your message..."
                        errorMessage={errors.message?.message?.toString()}
                        variant="outlined"
                        fullWidth
                        multiline
                        rows={4}
                        control={control}
                      />
                    )}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <Button
                    type="submit"
                    className={`rounded-full px-14 ${
                      isValid && !isSubmitting
                        ? 'bg-blue-primary hover:bg-blue-primary-hover text-white'
                        : 'bg-gray-300 text-gray-500'
                    }`}
                    disabled={!isValid || isSubmitting}
                  >
                    Send
                  </Button>
                </div>
                <div className="flex flex-col items-start">
                  <p className="text-gray-500 text-[12px] mt-40">
                    By clicking the button, you agree to our{' '}
                    <a
                      href="https://www.designkey.studio/terms-conditions"
                      target="_blank"
                      className="underline"
                    >
                      Terms of Use
                    </a>{' '}
                    and{' '}
                    <a
                      href="https://www.designkey.studio/privacy-policy"
                      target="_blank"
                      className="underline"
                    >
                      Privacy Policy
                    </a>
                    .
                  </p>
                  <p className="text-gray-500 text-[12px] mt-12">
                    Design Key is a tech agency for modern business growth. Design, build, and scale
                    software with AI-driven efficiency, achieving 5x output while delivering
                    solutions that prioritize user experience and business impact.
                  </p>
                </div>
              </form>
            </FormProvider>
          </div>
        )}
      </DialogContent>
    </AppSimpleDialog>
  );
};

export default Popup;
